import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { Password } from "@/components/password";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { FormTooltip } from "@/components/ui/form-description";

interface PasswordFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  description?: string;
  descriptionAsTooltip?: boolean;
  label: string;
  name: TName;
  placeholder?: string;
  required?: boolean;
  requiredLabel?: string;
  visuallyHiddenLabel?: boolean;
  hidePasswordLabel?: string;
  showPasswordLabel?: string;
}

export function PasswordField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  description,
  descriptionAsTooltip = false,
  label,
  name,
  placeholder,
  required,
  requiredLabel = "Required",
  visuallyHiddenLabel = false,
  hidePasswordLabel,
  showPasswordLabel,
  className,
  ...htmlProps
}: Readonly<PasswordFieldProps<TFieldValues, TName>>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className} {...htmlProps}>
          <div className={cn(visuallyHiddenLabel ? "sr-only" : "")}>
            <FormLabel className="inline-block leading-snug">
              {label}
              {required ? (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              ) : null}
            </FormLabel>
            {description && !descriptionAsTooltip ? (
              <FormDescription>{description}</FormDescription>
            ) : null}
            {description && descriptionAsTooltip && (
              <FormTooltip description={description} />
            )}
          </div>
          <FormControl>
            <Password
              hidePasswordLabel={hidePasswordLabel}
              showPasswordLabel={showPasswordLabel}
              placeholder={placeholder}
              aria-required={required ? "true" : "false"}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
